<template>
    <div
        class="flex items-center gap-2 mt-1 px-3 py-2 rounded-lg border-l-4 bg-gray-50 text-sm text-gray-700"
        :class="borderColorClass"
        data-testid="appointment-card"
    >
        <icon :name="iconName" class="shrink-0 w-4 h-4" :class="iconColorClass" />

        <div class="flex flex-col">
            <span data-testid="appointment-status-text" class="font-medium">
                {{ t(statusKey) }}
            </span>
            <span
                data-testid="appointment-datetime"
                class="text-xs text-gray-500"
                v-if="formattedDateTime"
            >
                {{ formattedDateTime }}
            </span>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed } from 'vue';
    import { useI18n } from '@/hooks/useI18n.ts';
    import ActivixDate from '@/value-objects/ActivixDate.js';
    import type { AppointmentStatus } from '@/entities/types/ConversationType.ts';

    enum Status {
        CONFIRMED = 'confirmed',
        CANCELED = 'canceled',
        PENDING = 'pending',
    }

    const props = defineProps<{
        appointment: AppointmentStatus;
    }>();

    const { t } = useI18n();

    const status = computed<Status>(() => {
        if (props.appointment.confirmed) {
            return Status.CONFIRMED;
        }

        if (props.appointment.canceled) {
            return Status.CANCELED;
        }

        return Status.PENDING;
    });

    const statusKey = computed<string>(() => {
        return `smsCommunication.appointment.${status.value}`;
    });

    const borderColorClass = computed<string>(() => {
        const colorMap: Record<Status, string> = {
            [Status.CONFIRMED]: 'border-green-500',
            [Status.CANCELED]: 'border-red-500',
            [Status.PENDING]: 'border-yellow-500',
        };

        return colorMap[status.value];
    });

    const iconColorClass = computed<string>(() => {
        const colorMap: Record<Status, string> = {
            [Status.CONFIRMED]: 'text-green-500',
            [Status.CANCELED]: 'text-red-500',
            [Status.PENDING]: 'text-yellow-500',
        };

        return colorMap[status.value];
    });

    const iconName = computed<string>(() => {
        const iconMap: Record<Status, string> = {
            [Status.CONFIRMED]: 'regular/calendar-check',
            [Status.CANCELED]: 'regular/remove-circle',
            [Status.PENDING]: 'regular/calendar-clock',
        };

        return iconMap[status.value];
    });

    const formattedDateTime = computed<string | null>(() => {
        if (!props.appointment.startAt) {
            return null;
        }

        return new ActivixDate(props.appointment.startAt).toHumanShort();
    });
</script>
